"use client"

import { useTranslations } from "next-intl"

import { tv } from "@/lib/utils/tv"

/* 8px dots, 8px gap, a 30px active bar — 62px across at three slides, measured off
   Marketplace-Components (Figma 73:3634, 2026-08-26).

   This geometry KNOWINGLY fails WCAG 2.2 SC 2.5.8 (target size minimum, AA), and the
   previous one was built to pass it: 6px dots on an 18px gap put centers exactly 24px
   apart. At 8/8 they sit 16px apart, which clears neither of the criterion's two outs —
   the target is under 24×24 and the offset is under 24px. There is no geometry that
   satisfies both the design and the criterion; an expanded hit area only makes the
   24px circles overlap.

   The team took the design and the argument that the dots are a redundant control over
   a reel that already swipes and scrolls. Recorded in findings.md, 2026-08-26.

   What we keep: the hit area is 24px TALL (free, no visual cost) and exactly 16px wide,
   so adjacent targets meet without overlapping — an overlapping target would eat its
   neighbour's tap. */
const dots = tv({
  base: "flex gap-200 rounded-full",
  variants: {
    tone: {
      scrim: "bg-scrim px-250 py-150",
      inverted: "",
      plain: "",
    },
  },
  defaultVariants: { tone: "scrim" },
})

const DOT_TONE = {
  scrim: { active: "bg-on-scrim", idle: "bg-on-scrim-muted", ring: "focus-visible:ring-on-scrim" },
  inverted: { active: "bg-on-scrim", idle: "bg-on-scrim-muted", ring: "focus-visible:ring-on-scrim" },
  plain: { active: "bg-surface-inverse", idle: "bg-line", ring: "focus-visible:ring-brand" },
} as const

interface CarouselDotsProps {
  count: number
  index: number
  onChange: (index: number) => void
  tone?: keyof typeof DOT_TONE
  // Overrides the active dot's fill (a `bg-*` class) — the hero passes the
  // current slide's accent so the active dot tracks the headline color.
  activeColor?: string
  idleColor?: string
  window?: number
  className?: string
}

const dotWindow = (count: number, index: number, size?: number): [start: number, end: number] => {
  if (!size || count <= size) return [0, count]
  const start = Math.min(Math.max(index - Math.floor(size / 2), 0), count - size)
  return [start, start + size]
}

const dotSize = (dotIndex: number, index: number, [start, end]: [number, number], count: number) => {
  if (dotIndex === index) return "h-2 w-7.5"
  const clipped = (dotIndex === start && start > 0) || (dotIndex === end - 1 && end < count)
  return clipped ? "size-1.5 self-center" : "size-2"
}

export const CarouselDots = ({ count, index, onChange, tone = "scrim", activeColor, idleColor, window, className }: CarouselDotsProps) => {
  const t = useTranslations("common")
  const range = dotWindow(count, index, window)
  const [start, end] = range
  return (
    <div className={dots({ tone, className })}>
      {Array.from({ length: end - start }, (_, offset) => start + offset).map((dotIndex) => (
        <button
          key={dotIndex}
          type="button"
          aria-label={t("goToSlide", { index: dotIndex + 1 })}
          aria-current={dotIndex === index || undefined}
          onClick={() => onChange(dotIndex)}
          className={`transition-pill relative cursor-pointer rounded-full before:absolute before:-inset-x-1 before:-inset-y-2 focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none ${DOT_TONE[tone].ring} ${dotSize(dotIndex, index, range, count)} ${dotIndex === index ? (activeColor ?? DOT_TONE[tone].active) : (idleColor ?? DOT_TONE[tone].idle)}`}
        />
      ))}
    </div>
  )
}
